import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { getGifts, sendGift } from '../services/api';

/**
 * Small bottom-sheet modal that lets a user pick a gift and send it to `recipientId`.
 * `onSent` fires after a successful send.
 */
const GiftPickerModal = ({ recipientId, recipientName, onClose, onSent, userCredits = 0 }) => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getGifts().then(r => setGifts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSend = async () => {
    if (!picked) return;
    setSending(true);
    setError('');
    try {
      await sendGift(picked.gift_id, recipientId, msg.trim());
      onSent?.(picked);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send gift');
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="gift-picker-backdrop"
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 250 }}
          className="relative z-[101] w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl max-h-[85vh] mb-[calc(64px+env(safe-area-inset-bottom,0px))] sm:mb-0 overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="gift-picker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div>
              <div className="font-bold text-gray-900">Send a gift</div>
              {recipientName && <div className="text-xs text-gray-500">to {recipientName}</div>}
            </div>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100" data-testid="gift-close-btn">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Balance */}
          <div className="px-4 pt-3 text-xs text-gray-500">Your balance: <span className="font-semibold text-gray-800">{userCredits} credits</span></div>

          {/* Gifts grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-pink-500 animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {gifts.map(g => {
                  const affordable = userCredits >= g.cost_credits;
                  const isPicked = picked?.gift_id === g.gift_id;
                  return (
                    <button
                      key={g.gift_id}
                      onClick={() => affordable && setPicked(g)}
                      disabled={!affordable}
                      className={`aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                        isPicked
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-100 bg-white hover:border-pink-200'
                      } ${!affordable ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid={`gift-option-${g.gift_id}`}
                    >
                      <div className="text-3xl">{g.emoji}</div>
                      <div className="text-[11px] font-medium text-gray-800 truncate max-w-full px-1">{g.name}</div>
                      <div className="text-[10px] text-pink-600 font-bold">{g.cost_credits} cr</div>
                    </button>
                  );
                })}
              </div>
            )}

            {picked && (
              <div className="mt-4">
                <Textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value.slice(0, 280))}
                  placeholder="Add a personal note (optional)"
                  className="min-h-[70px] rounded-xl resize-none"
                  data-testid="gift-note-input"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{msg.length}/280</p>
              </div>
            )}

            {error && (
              <div className="mt-3 bg-red-50 text-red-600 rounded-xl p-3 text-sm" data-testid="gift-error">{error}</div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 p-4">
            <Button
              onClick={handleSend}
              disabled={!picked || sending}
              className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-semibold"
              data-testid="gift-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {picked ? `Send for ${picked.cost_credits} credits` : 'Pick a gift'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GiftPickerModal;
